// Market tab — Story #6837 Stream 8a-1b: query, country state, sub-nav and
// the active panel, with the auto-run hook (8a-1c) and its status banner.
import { useState } from 'react'
import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { getMarket } from '../../services/api'
import type { MarketPanelProps } from '../../types/market'
import SectorsPanel from './SectorsPanel'
import OutlookPanel from './OutlookPanel'
import PestleSwotPanel from './PestleSwotPanel'
import { useMarketAutoRun, type MarketRunPhase } from './useMarketAutoRun'

type MarketView = 'sectors' | 'outlook' | 'pestle-swot'

const PHASE_TEXT: Partial<Record<MarketRunPhase, string>> = {
  classifying: 'Categorising this invention\'s sector…',
  pestle: 'Researching the PESTLE outlook…',
  swot: 'Building the SWOT…',
  range: 'Estimating the market range…',
  running: 'Still running on the server — checking every few seconds…',
}

const VIEWS: { key: MarketView; label: string }[] = [
  { key: 'sectors', label: 'Sectors' },
  { key: 'outlook', label: 'Outlook' },
  { key: 'pestle-swot', label: 'PESTLE & SWOT' },
]

export default function MarketTab({ projectId }: { projectId: string }) {
  const [view, setView] = useState<MarketView>('sectors')
  const [country, setCountry] = useState('AUS')

  // keepPreviousData: switching country keeps the current view on screen while the new one loads, instead of
  // blanking the panels (which also unmounts the country chips, the unit toggle and any open edit).
  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['market', projectId, country],
    queryFn: () => getMarket(projectId, country),
    enabled: !!projectId,
    placeholderData: keepPreviousData,
  })

  // Rules of hooks: called directly after useQuery, before any early return.
  const auto = useMarketAutoRun({ projectId, market: data, country, canRun: data?.can_run === true })

  const subNav = (
    <div className="flex gap-0.5 bg-sky-500/10 rounded-lg p-0.5 w-fit" role="tablist" aria-label="Market view">
      {VIEWS.map((v) => (
        <button
          key={v.key}
          role="tab"
          aria-selected={view === v.key}
          onClick={() => setView(v.key)}
          data-testid={`market-subnav-${v.key}`}
          className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${
            view === v.key ? 'bg-white text-iflow-700 shadow-sm' : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          {v.label}
        </button>
      ))}
    </div>
  )

  let body = null
  if (isLoading) {
    body = <div data-testid="market-loading" className="text-sm text-gray-500 py-6">Loading market view…</div>
  } else if (isError || !data) {
    body = (
      <div data-testid="market-error" role="alert" className="text-sm text-red-600 py-6">
        The market view could not be loaded.
        <button type="button" data-testid="market-error-retry" onClick={() => void refetch()} className="ml-2 font-semibold underline">Try again</button>
        {country !== 'AUS' && (
          <button type="button" data-testid="market-error-back" onClick={() => setCountry('AUS')} className="ml-2 font-semibold underline">Back to Australia</button>
        )}
      </div>
    )
  } else {
    const panelProps: MarketPanelProps = {
      projectId,
      market: data,
      country,
      onCountryChange: setCountry,
      canRun: data.can_run === true,
    }
    body =
      view === 'sectors' ? <SectorsPanel {...panelProps} />
      : view === 'outlook' ? <OutlookPanel {...panelProps} />
      : <PestleSwotPanel {...panelProps} />
  }

  return (
    <div data-testid="market-tab" className="space-y-3">
      {subNav}
      {(PHASE_TEXT[auto.phase] || auto.phase === 'rate_limited' || auto.phase === 'error') && (
        <div
          data-testid="market-autorun-banner"
          role="status"
          className={`text-xs rounded-lg px-3 py-2 flex items-center justify-between gap-3 ${
            auto.phase === 'error' ? 'bg-red-50 text-red-700' : 'bg-sky-50 text-iflow-700'
          }`}
        >
          <span>
            {auto.phase === 'rate_limited'
              ? `Too many market analyses right now${auto.retryAfter != null ? ` — try again in ${auto.retryAfter} s` : ''}.`
              : auto.phase === 'error'
                ? auto.error
                : PHASE_TEXT[auto.phase]}
          </span>
          {(auto.phase === 'error' || auto.phase === 'rate_limited') && (
            <button
              type="button"
              onClick={auto.retry}
              data-testid="market-autorun-retry"
              className="font-medium underline"
            >
              Retry
            </button>
          )}
        </div>
      )}
      {body}
    </div>
  )
}
